



















































































import Vue from 'vue';
import Component from 'vue-class-component';
import { Model, Prop, Watch } from 'vue-property-decorator';
import TextInput from '@components/020-TextInput/TextInput.vue';
import SelectInput from '@components/021-SelectInput/SelectInput.vue';
import ISelectOption from '@frontend/types/base/ISelectOption';

interface IValueSet {
	office: string;
	jobType: string;
	jobField: string;
}

enum Field {
	Office = 'office',
	JobType = 'jobType',
	JobField = 'jobField'
}

@Component({
	components: {
		SelectInput,
		TextInput
	}
})
export default class JobSelection extends Vue {
	// Provide enum to template
	protected Field: any = Field;

	@Model('change', { type: Object, default: () => ({ office: '', jobType: '', jobField: '' }) })
	protected readonly value!: IValueSet;

	@Prop(String)
	protected readonly id!: string;

	@Prop({ type: String, default: '' })
	protected readonly name!: string;

	@Prop(String)
	protected readonly label?: string;

	@Prop({ type: String, default: 'text' })
	protected readonly type!: string;

	@Prop({ type: Array })
	protected readonly optionSets!: IValueSet[];

	@Prop({ type: Object })
	protected readonly jobSelectionFieldConfig!: Record<string, string>;

	@Prop({ type: Object, default: null })
	protected readonly error!: string | Record<string, string> | null;

	protected get officeOptions(): ISelectOption[] {
		const options: string[] = this.optionSets.map((optionSet: IValueSet): string => optionSet.office);

		return options
			.filter((option: string, index: number): boolean => options.indexOf(option) === index)
			.map((option: string): ISelectOption => ({ label: option, value: option }));
	}

	protected get jobTypeOptions(): ISelectOption[] {
		const options: string[] = this.optionSets
			.filter((optionSet: IValueSet): boolean => !!this.value && optionSet.office === this.office)
			.map((optionSet: IValueSet): string => optionSet.jobType);

		return options
			.filter((option: string, index: number): boolean => options.indexOf(option) === index)
			.map((option: string): ISelectOption => ({ label: option, value: option }));
	}

	protected get jobFieldOptions(): ISelectOption[] {
		const options: string[] = this.optionSets
			.filter(
				(optionSet: IValueSet): boolean =>
					!!this.value && optionSet.office === this.office && optionSet.jobType === this.jobType
			)
			.map((optionSet: IValueSet): string => optionSet.jobField);

		return options
			.filter((option: string, index: number): boolean => options.indexOf(option) === index)
			.map((option: string): ISelectOption => ({ label: option, value: option }));
	}

	protected get jobTypePrompt(): string {
		if (this.office === '') return this.i18n.pleaseChooseOffice;
		return this.jobSelectionFieldConfig[Field.JobType];
	}

	protected get jobFieldPrompt(): string {
		if (this.office === '') return this.i18n.pleaseChooseOffice;
		if (this.jobType === '') return this.i18n.pleaseChooseJobType;
		return this.jobSelectionFieldConfig[Field.JobField];
	}

	protected get office(): string {
		return this.value[Field.Office];
	}

	protected set office(office: string) {
		this.$emit('change', {
			...this.value,
			[Field.Office]: office
		});
	}

	protected get jobType(): string {
		return this.value[Field.JobType];
	}

	protected set jobType(jobType: string) {
		this.$emit('change', {
			...this.value,
			[Field.JobType]: jobType
		});
	}

	protected get jobField(): string {
		return this.value[Field.JobField];
	}

	protected set jobField(jobField: string) {
		this.$emit('change', {
			...this.value,
			[Field.JobField]: jobField
		});
	}

	protected get internalErrors(): Record<Field, string | null> {
		if (!this.error || typeof this.error === 'string') {
			// In case the error is a string, it will be handled in FormInput for the input as a whole
			return {
				[Field.Office]: null,
				[Field.JobType]: null,
				[Field.JobField]: null
			};
		}

		return this.error;
	}

	protected onInvalid(field: string, event: Event): void {
		const input: HTMLInputElement = event.currentTarget as HTMLInputElement;

		this.$emit('invalid', {
			...this.internalErrors,
			[field]: input.validationMessage
		});
	}

	@Watch('office')
	protected onOfficeChanged(): void {
		if (!this.jobTypeOptions.some((option: ISelectOption): boolean => option.value === this.jobType)) {
			// Selected jobType not found in the newly selected office
			this.jobType = '';
		} else if (!this.jobFieldOptions.some((option: ISelectOption): boolean => option.value === this.jobField)) {
			// Selected jobField not found in the newly selected office
			this.jobField = '';
		}
	}

	@Watch('jobType')
	protected onJobTypeChanged(): void {
		if (!this.jobFieldOptions.some((option: ISelectOption): boolean => option.value === this.jobField)) {
			// Selected jobField not found in the newly selected jobType
			this.jobField = '';
		}
	}
}
